import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { useTerritories } from '../../context/TerritoriesContext';
import { useConfig } from '../../context/ConfigContext';
import { subscribeLeads } from '../../data/listeners/leads';
import { customerVisits } from '../../data/pipeline';
import {
  HEALTH, HEALTH_COLOR, COVERAGE_DAYS, leadValueOf,
} from '../../data/leadConstants';
import Icon from '../../components/Icon';
import { useIsMobile } from '../../hooks/useIsMobile';

const fmt = (ms) => (ms ? new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '— schedule —');
const peso = (n) => `₱${Math.round(n).toLocaleString()}`;
const WEEK = 7 * 86400000;
const HEALTH_RANK = Object.fromEntries(HEALTH.map((h, i) => [h, i])); // Healthy<At-risk<Churned

const SORTS = {
  next: { label: 'Next visit', get: (c) => c.nextVisit ?? Infinity },
  health: { label: 'Account health', get: (c) => HEALTH_RANK[c.health] ?? 0 },
  php: { label: '₱/mo', get: (c) => c.leadValue },
  name: { label: 'Account name', get: (c) => (c.businessName ?? '').toLowerCase() },
};

// Post-Won maintenance: customers to service. Two tabs (CRM revisions Task 3):
//   • Service schedule — health + next visit (the original maintenance view).
//   • Active customers — full filter set (territory/category/tier/health/coverage
//     day + search), with Coverage Day shown and filterable.
export default function CustomerVisits() {
  const { tenantId, role, territoryIds } = useAuth();
  const { openLead, openLogVisit } = useUI();
  const isMobile = useIsMobile();
  const { territories, terrLabel } = useTerritories();
  const { config } = useConfig();
  const [leads, setLeads] = useState([]);
  const [tab, setTab] = useState('schedule'); // 'schedule' | 'active'
  const [q, setQ] = useState('');
  const [sortKey, setSortKey] = useState('next');
  const [sortDir, setSortDir] = useState('asc'); // next-visit reads best ascending
  const [filters, setFilters] = useState({
    territory: '', category: '', tier: '', health: '', coverageDay: '',
  });

  useEffect(() => subscribeLeads({ tenantId, role, territoryIds }, setLeads), [tenantId, role, territoryIds]);

  const all = useMemo(() => customerVisits(leads), [leads]);

  const scheduleRows = useMemo(() => {
    const get = SORTS[sortKey].get;
    const dir = sortDir === 'asc' ? 1 : -1;
    return all
      .filter((c) => !q || (c.businessName ?? '').toLowerCase().includes(q.toLowerCase()))
      .slice()
      .sort((a, b) => {
        const av = get(a); const bv = get(b);
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
  }, [all, q, sortKey, sortDir]);

  const activeRows = useMemo(() => all.filter((c) =>
    (!filters.territory || c.territoryId === filters.territory)
    && (!filters.category || c.category === filters.category)
    && (!filters.tier || c.tier === filters.tier)
    && (!filters.health || c.health === filters.health)
    && (!filters.coverageDay || (c.coverageDays || []).includes(filters.coverageDay))
    && (!q || (c.businessName ?? '').toLowerCase().includes(q.toLowerCase()))), [all, filters, q]);

  const stats = useMemo(() => {
    const now = Date.now();
    return {
      active: all.length,
      due: all.filter((c) => c.nextVisit && c.nextVisit >= now && c.nextVisit < now + WEEK).length,
      php: all.reduce((a, c) => a + c.leadValue, 0),
      atRisk: all.filter((c) => c.health && c.health !== 'Healthy').length,
    };
  }, [all]);

  const setF = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  return (
    <div>
      <div className="stat-row">
        <div className="stat"><div className="n">{stats.active}</div><div className="l">Active accounts</div></div>
        <div className="stat"><div className="n">{stats.due}</div><div className="l">Visits due this week</div></div>
        <div className="stat"><div className="n" style={{ color: 'var(--presto-success)' }}>{peso(stats.php)}</div><div className="l">Recurring revenue / mo</div></div>
        <div className="stat"><div className="n" style={{ color: 'var(--presto-error)' }}>{stats.atRisk}</div><div className="l">At-risk</div></div>
      </div>

      <div className="toolbar">
        <div className="seg" role="group" aria-label="Customer view">
          <button type="button" className={tab === 'schedule' ? 'on' : ''} onClick={() => setTab('schedule')}>Service schedule</button>
          <button type="button" className={tab === 'active' ? 'on' : ''} onClick={() => setTab('active')}>Active customers</button>
        </div>
        <div className="searchbox"><Icon name="search" size={16} /><input placeholder="Search accounts…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        {tab === 'schedule' ? (
          <>
            <div className="field"><label>Sort by</label>
              <select className="input" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>{s.label}</option>)}
              </select>
            </div>
            <button type="button" className="btn btn-ghost sortbtn" onClick={() => setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'))}>
              <Icon name={sortDir === 'desc' ? 'sortdesc' : 'sortasc'} size={16} /> {sortDir === 'desc' ? 'Desc' : 'Asc'}
            </button>
          </>
        ) : (
          <>
            <select className="chip-select" value={filters.territory} onChange={setF('territory')}>
              <option value="">All territories</option>
              {territories.map((t) => <option key={t.id} value={t.id}>{t.label ?? t.id}</option>)}
            </select>
            <select className="chip-select" value={filters.category} onChange={setF('category')}>
              <option value="">All channels</option>
              {(config.channels ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="chip-select" value={filters.tier} onChange={setF('tier')}>
              <option value="">All tiers</option>
              {(config.tiers ?? []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="chip-select" value={filters.health} onChange={setF('health')}>
              <option value="">All health</option>
              {HEALTH.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <select className="chip-select" value={filters.coverageDay} onChange={setF('coverageDay')}>
              <option value="">All coverage days</option>
              {COVERAGE_DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--color-fg-muted)', fontSize: 'var(--text-small)' }}>
          {tab === 'schedule' ? scheduleRows.length : activeRows.length} of {all.length}
        </span>
      </div>

      {isMobile ? (
        <div className="lead-cards">
          {(tab === 'schedule' ? scheduleRows : activeRows).map((c) => (
            <div key={c.id} className="lead-card" onClick={() => openLead(c.id)}>
              <div className="lc-top">
                <span className="lc-name">{c.businessName}</span>
                <span className="badge" style={{ background: `${HEALTH_COLOR[c.health]}1a`, color: HEALTH_COLOR[c.health] }}>
                  <span className="dot" style={{ background: HEALTH_COLOR[c.health] }} />{c.health}
                </span>
              </div>
              <div className="lc-meta">
                <span>{terrLabel(c.territoryId)}</span>
                {tab === 'schedule' ? (
                  <>
                    <span>{c.serviceCadence ?? '—'}</span>
                    <span>Next: {fmt(c.nextVisit)}</span>
                  </>
                ) : (
                  <>
                    {c.category && <span>{c.category}</span>}
                    {c.tier && <span>{c.tier}</span>}
                    {(c.coverageDays || []).length > 0 && <span>{c.coverageDays.join(', ')}</span>}
                  </>
                )}
                <span>{peso(c.leadValue)}/mo</span>
              </div>
              <div style={{ marginTop: 'var(--space-2)' }}>
                <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); openLogVisit(c.id); }}>
                  <Icon name="logvisit" size={14} /> Log Touchpoint
                </button>
              </div>
            </div>
          ))}
          {(tab === 'schedule' ? scheduleRows : activeRows).length === 0 && (
            <div style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>
              {tab === 'schedule' ? 'No customers to service yet — close a deal to start a service relationship.' : 'No active customers match these filters.'}
            </div>
          )}
        </div>
      ) : (
      <div className="table-wrap">
        {tab === 'schedule' ? (
          <table>
            <thead>
              <tr><th>Account</th><th>Territory</th><th>Health</th><th>Cadence</th><th>Last visit</th><th>Next visit</th><th>₱/mo</th><th /></tr>
            </thead>
            <tbody>
              {scheduleRows.map((c) => (
                <tr key={c.id} className="click" onClick={() => openLead(c.id)}>
                  <td style={{ fontWeight: 'var(--font-semibold)' }}>{c.businessName}</td>
                  <td>{terrLabel(c.territoryId)}</td>
                  <td>
                    <span className="badge" style={{ background: `${HEALTH_COLOR[c.health]}1a`, color: HEALTH_COLOR[c.health] }}>
                      <span className="dot" style={{ background: HEALTH_COLOR[c.health] }} />{c.health}
                    </span>
                  </td>
                  <td>{c.serviceCadence ?? '—'}</td>
                  <td className="muted">{fmt(c.lastServiceAt)}</td>
                  <td><b>{fmt(c.nextVisit)}</b></td>
                  <td className="muted">{peso(c.leadValue)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); openLogVisit(c.id); }}>
                      <Icon name="logvisit" size={14} /> Log Touchpoint
                    </button>
                  </td>
                </tr>
              ))}
              {scheduleRows.length === 0 && (
                <tr><td colSpan={8} style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No customers to service yet — close a deal to start a service relationship.</td></tr>
              )}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr><th>Account</th><th>Territory</th><th>Channel</th><th>Tier</th><th>Coverage days</th><th>Health</th><th>₱/mo</th><th /></tr>
            </thead>
            <tbody>
              {activeRows.map((c) => (
                <tr key={c.id} className="click" onClick={() => openLead(c.id)}>
                  <td style={{ fontWeight: 'var(--font-semibold)' }}>{c.businessName}</td>
                  <td>{terrLabel(c.territoryId)}</td>
                  <td>{c.category ?? '—'}</td>
                  <td>{c.tier ? <span className={`badge tier-${c.tier}`}>{c.tier}</span> : '—'}</td>
                  <td>{(c.coverageDays || []).length ? c.coverageDays.join(', ') : <span className="muted">— none —</span>}</td>
                  <td>
                    <span className="badge" style={{ background: `${HEALTH_COLOR[c.health]}1a`, color: HEALTH_COLOR[c.health] }}>
                      <span className="dot" style={{ background: HEALTH_COLOR[c.health] }} />{c.health}
                    </span>
                  </td>
                  <td className="muted">{peso(c.leadValue)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn btn-primary btn-sm" onClick={(e) => { e.stopPropagation(); openLogVisit(c.id); }}>
                      <Icon name="logvisit" size={14} /> Log Touchpoint
                    </button>
                  </td>
                </tr>
              ))}
              {activeRows.length === 0 && (
                <tr><td colSpan={8} style={{ color: 'var(--color-fg-muted)', textAlign: 'center', padding: 'var(--space-8)' }}>No active customers match these filters.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}
